import {
  Button,
  FormControl,
  MenuItem,
  Modal,
  Select,
  TextField,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { RxCross1 } from "react-icons/rx";
import { useEffect, useState, version } from "react";
import { HttpService } from "../../service/HttpService";
import FileInput from "../FileInput/FileInput";
import { useAuth } from "../../context/AuthProvider";
import SelectInput from "../SelectInput/SelectInput";
import { PiUploadSimpleThin } from "react-icons/pi";

const AddEditInventory = ({
  open,
  isAdd,
  fetchInventory,
  inventory_id,
  handleClose,
}) => {
  const { setShowLoginPopup, showLoginPopup } = useAuth();
  const httpService = new HttpService(setShowLoginPopup);
  const [loading, setLoading] = useState(false);
  const [departments, setDepartment] = useState([]);
  const [units, setUnits] = useState([]);

  const fetchDepartment = () => {
    setLoading(true);
    httpService
      .get("inventory/departments")
      .then((response) => {
        setDepartment(response.data); // Assuming the response is an array of rows
        setTimeout(() => {
          setLoading(false);
        }, 300);
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error fetching data:", error);
      });
  };

  const fetchUnits = () => {
    setLoading(true);
    httpService
      .get("inventory/units")
      .then((response) => {
        setUnits(response.data); // Assuming the response is an array of rows
        setTimeout(() => {
          setLoading(false);
        }, 300);
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error fetching data:", error);
      });
  };

  useEffect(() => {
    fetchDepartment();
    fetchUnits();
  }, [showLoginPopup]);

  useEffect(() => {
    if (open && !isAdd && inventory_id) {
      fetchInventoryData(inventory_id);
    }
  }, [open, isAdd, inventory_id]);

  const fetchInventoryData = async (id) => {
    try {
      const response = await httpService.get(`inventory/${id}`);
      const data = response?.data;

      if (data) {
        formik.setValues({
          name: data.item_name || "",
          department_id: data.department_id || "",
          stored_location: data.stored_location || "",
          reorder_threshold: data.reorder_threshold || "",
          reorder_amount: data.reorder_amount || "",
          on_hand_qty: data.on_hand_qty || "",
          feed_rate_per_week: data.feed_rate_per_week || "",
          unit: data.unit_id || "",
          image_file_path: "", // You can keep file input empty initially
          item_description: data.item_description || "",
          supplier: data.supplier || "",
        });
      }
    } catch (error) {
      console.log("Error fetching inventory data:", error);
    }
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      department_id: "",
      stored_location: "",
      reorder_threshold: "",
      on_hand_qty: 0,
      image_file_path: "",
      feed_rate_per_week: "",
      unit: "",
      item_description: "",
      supplier: "",
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .trim()
        .required("Please Enter Inventory Name")
        .max(200, "Inventory Name must be less than 200 characters"),
      stored_location: Yup.string().required("Please Enter Stored Location"),
      // reorder_threshold: Yup.number().required(
      //   "Please Enter Reorder Threshold"
      // ),
      department_id: Yup.number().required("Please Select Department"),
      // reorder_amount: Yup.number().required("Please Enter Reorder Amount"),
      // on_hand_qty: Yup.number().required("Please Enter Quantity"),
      image_file_path: Yup.mixed()
        // .required("Attachment Is Required")
        .test("fileSize", "Attachment size must be less than 10MB", (value) => {
          if (!value || typeof value !== "object") return true; // Skip if no files provided or value is not an object
          const files = Object.values(value); // Convert object values to an array of files
          return files.every((file) => file.size <= 10485760); // Check size of each file
        })
        .test("fileFormat", "Invalid file format", (value) => {
          if (!value || typeof value !== "object") return true; // Skip if no files provided or value is not an object
          const files = Object.values(value); // Convert object values to an array of files
          if (files.length === 0) return true; // Skip if no files provided
          const allowedExtensions = {
            video: ["mp4", "webm", "mov"],
            pdf: ["pdf"],
            document: ["doc", "docx"],
            spreadsheet: ["xls", "xlsx", "csv"],
            image: ["jpg", "png", "jpeg", "gif", "webp"],
          };
          return files.every((file) => {
            if (!file || !file.name) return false; // If file or file name is undefined, return false
            const fileExtension = file?.name?.split(".").pop().toLowerCase();
            const fileType = Object.keys(allowedExtensions).find((type) =>
              allowedExtensions[type].includes(fileExtension)
            );
            return !!fileType;
          });
        }),
      item_description: Yup.string(),
      supplier: Yup.string(),
    }),
    onSubmit: async (values) => {
      const formData = new FormData();
      formData.append("name", values.name || "");
      formData.append("stored_location", values.stored_location || "");
      formData.append("on_hand_qty", values.on_hand_qty || "");
      formData.append("reorder_threshold", values.reorder_threshold || "");
      formData.append("reorder_amount", values.reorder_amount || "");
      formData.append("department_id", values.department_id || "");
      formData.append("image_file_path", values.image_file_path || "");
      formData.append("unit", values.unit || "");
      formData.append("feed_rate_per_week", values.feed_rate_per_week || "");
      formData.append("item_description", values.item_description || "");
      formData.append("supplier", values.supplier || "");

      try {
        if (isAdd) {
          const response = await httpService.post("inventory/", formData);
          if (response) {
            toast.success("Inventory Added Successfully", {
              position: "top-right",
            });
            formik.resetForm();
            handleClose();
            fetchInventory();
          }
        } else {
          const res = await httpService.put(
            `inventory/${inventory_id}`,
            formData
          );
          if (res) {
            toast.success("Inventory Updated Successfully", {
              position: "top-right",
            });
            handleClose();
            fetchInventory();
          }
        }
      } catch (error) {
        console.log(error);
      }
    },
  });

  return (
    <Modal
      className="task-modal"
      open={open}
      onClose={() => {
        handleClose();
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <form onSubmit={formik.handleSubmit} encType="multipart/form-data">
        <div className="modal_block">
          <div className="modal_header">
            <div className="modal_title_wrapper">
              <h2 className="modal_title">
                {isAdd === true ? "Add Inventory" : "Edit Inventory"}
              </h2>
            </div>
            <div className="cancel-btn-wrap">
              <RxCross1
                onClick={() => {
                  handleClose();
                }}
              />
            </div>
          </div>
          <div className="modal_content">
            <div className="row">
              <div className="item_increase">
                <div className="row">
                  <div className="col-md-6">
                    <div className="form_field_wrapper">
                      <label>
                        Name<span style={{ color: "red" }}>*</span>
                      </label>
                      <TextField
                        id="name"
                        InputProps={{}}
                        placeholder="Enter Inventory Name"
                        variant="outlined"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.name}
                      />
                      {formik.touched.name && formik.errors.name ? (
                        <div style={{ color: "red" }}>{formik.errors.name}</div>
                      ) : null}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <SelectInput
                      label="Department"
                      name="department_id"
                      value={formik.values.department_id}
                      options={departments}
                      multiple={false}
                      onChange={(event) => {
                        formik.setFieldValue(
                          "department_id",
                          event.target.value
                        );
                      }}
                      onBlur={(e) => formik.handleBlur(e)}
                      error={
                        formik.touched.department_id &&
                        formik.errors.department_id
                      }
                      required={true}
                      displayEmpty
                    />
                  </div>
                  {/* New fields after Department */}
                  <div className="col-md-6">
                    <div className="form_field_wrapper">
                      <label>Item Description</label>
                      <TextField
                        name="item_description"
                        type="text"
                        placeholder="Enter item description"
                        value={formik.values.item_description}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.item_description &&
                      formik.errors.item_description ? (
                        <div style={{ color: "red" }}>
                          {formik.errors.item_description}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form_field_wrapper">
                      <label>Supplier Name</label>
                      <TextField
                        name="supplier"
                        type="text"
                        placeholder="Enter supplier"
                        value={formik.values.supplier}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.supplier && formik.errors.supplier ? (
                        <div style={{ color: "red" }}>
                          {formik.errors.supplier}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form_field_wrapper">
                      <label>
                        Stored location
                        <span style={{ color: "red" }}>*</span>
                      </label>
                      <TextField
                        name="stored_location"
                        type="text"
                        InputProps={{ inputProps: { min: 0 } }}
                        placeholder="Enter Stored location"
                        value={formik.values.stored_location}
                        onChange={(event) => {
                          formik.setFieldValue(
                            "stored_location",
                            event.target.value
                          );
                        }}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.stored_location &&
                      formik.errors.stored_location ? (
                        <div style={{ color: "red" }}>
                          {formik.errors.stored_location}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  {/* <div className="col-md-6">
                    <div className="form_field_wrapper">
                      <label>
                        Total Quantity
                        <span style={{ color: "red" }}>*</span>
                      </label>
                      <TextField
                        name="on_hand_qty"
                        type="number"
                        InputProps={{
                          inputProps: {
                            step: "any", // Allows decimal/point values
                          },
                        }}
                        placeholder="Enter Quantity"
                        value={formik.values.on_hand_qty}
                        onChange={(event) => {
                          formik.setFieldValue(
                            "on_hand_qty",
                            event.target.value
                          );
                        }}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.on_hand_qty &&
                      formik.errors.on_hand_qty ? (
                        <div style={{ color: "red" }}>
                          {formik.errors.on_hand_qty}
                        </div>
                      ) : null}
                    </div>
                  </div> */}
                  <div className="col-md-6">
                    <div className="form_field_wrapper">
                      <label>
                        Reorder Threshold
                        {/* <span style={{ color: "red" }}>*</span> */}
                      </label>
                      <TextField
                        name="reorder_threshold"
                        type="number"
                        InputProps={{
                          inputProps: {
                            step: "any", // Allows decimal/point values
                          },
                        }}
                        placeholder="Enter Reorder Threshold"
                        value={
                          formik.values.reorder_threshold
                            ? parseFloat(formik.values.reorder_threshold)
                            : ""
                        }
                        onChange={(event) => {
                          formik.setFieldValue(
                            "reorder_threshold",
                            event.target.value
                          );
                        }}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.reorder_threshold &&
                      formik.errors.reorder_threshold ? (
                        <div style={{ color: "red" }}>
                          {formik.errors.reorder_threshold}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form_field_wrapper">
                      <label>
                        Reorder Amount
                        {/* <span style={{ color: "red" }}>*</span> */}
                      </label>
                      <TextField
                        name="reorder_amount"
                        type="number"
                        InputProps={{
                          inputProps: {
                            step: "any", // Allows decimal/point values
                          },
                        }}
                        placeholder="Enter Reorder Amount"
                        value={
                          formik.values.reorder_amount
                            ? parseFloat(formik.values.reorder_amount)
                            : ""
                        }
                        onChange={(event) => {
                          formik.setFieldValue(
                            "reorder_amount",
                            event.target.value
                          );
                        }}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.reorder_amount &&
                      formik.errors.reorder_amount ? (
                        <div style={{ color: "red" }}>
                          {formik.errors.reorder_amount}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form_field_wrapper">
                      <label>
                        Unit Price
                        {/* <span style={{ color: "red" }}>*</span> */}
                      </label>
                      <TextField
                        name="feed_rate_per_week"
                        type="number"
                        InputProps={{
                          inputProps: {
                            step: "any", // Allows decimal/point values
                          },
                        }}
                        placeholder="Enter Unit Price"
                        value={formik.values.feed_rate_per_week}
                        onChange={(event) => {
                          formik.setFieldValue(
                            "feed_rate_per_week",
                            event.target.value
                          );
                        }}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.feed_rate_per_week &&
                      formik.errors.feed_rate_per_week ? (
                        <div style={{ color: "red" }}>
                          {formik.errors.feed_rate_per_week}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <SelectInput
                      label="Unit"
                      name="unit"
                      value={formik.values.unit ? formik.values.unit : ""}
                      options={units}
                      multiple={false}
                      onChange={(event) => {
                        formik.setFieldValue("unit", event.target.value);
                      }}
                      onBlur={(e) => formik.handleBlur(e)}
                      error={formik.touched.unit && formik.errors.unit}
                      required={false}
                      displayEmpty
                    />
                  </div>
                  <div className="col-md-12">
                    <div className="form_field_wrapper">
                      <label>Profile Image</label>
                      <div className="upload-btn-wrap">
                        <Button
                          component="label"
                          role={undefined}
                          variant="contained"
                          tabIndex={-1}
                          startIcon={<PiUploadSimpleThin />}
                        >
                          <input
                            className="file-type"
                            type="file"
                            accept="image/*"
                            name="image_file_path"
                            onChange={(e) => {
                              formik.setFieldValue(
                                "image_file_path",
                                e.currentTarget.files[0]
                              );
                            }}
                            handleBlur={formik.handleBlur}
                          />
                          {formik.values.image_file_path ? (
                            <span>{formik.values.image_file_path?.name}</span> // Displaying the name of the image
                          ) : (
                            <span>Upload Profile Image</span>
                          )}
                        </Button>
                      </div>
                      {formik.errors.image_file_path &&
                        formik.touched.image_file_path && (
                          <span style={{ color: "red" }}>
                            {formik.errors.image_file_path}
                          </span>
                        )}
                    </div>
                  </div>
                  {/* <FileInput
                    file={formik.values.file}
                    attachment={formik.values.attached}
                    handleBlur={formik.handleBlur}
                    name="file"
                    name2="attached"
                    setFieldValue={(fieldName, value) =>
                      formik.setFieldValue(fieldName, value)
                    }
                  /> */}
                  {/* {formik.errors.file && formik.touched.file && (
                    <span style={{ color: "red" }}>{formik.errors.file}</span>
                  )} */}
                </div>
              </div>
            </div>
          </div>
          <div className="modal_footer">
            <button
              onClick={() => {
                handleClose();
              }}
              className="btn btn-border"
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {isAdd === true ? "Save" : "Update"}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default AddEditInventory;
